/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Features section for AI Removal - Powerful Features for Perfect Video Cleanup
 */
import Image from 'next/image';
import React from 'react';

import { scrollToTop } from '@/utils/landing/scrollToTop';

interface FeatureItem {
  title: string;
  description: string;
  imageUrl: string;
  buttonText: string;
}

const features: FeatureItem[] = [
  {
    title: 'Remove Subtitles and Captions Instantly',
    description:
      "When videos feel cluttered with text, our subtitle remover gives you a clean starting point that's easier to edit and repurpose. It identifies subtitles and captions across different styles and languages, clearing them from every frame with consistent accuracy. Clean frames make it easier to reuse footage for training, marketing, or multilingual versions without re‑recording anything. Teams can update lessons, product demos, or onboarding materials quickly because the visuals stay uniform across the entire timeline. The result is a polished video that's ready for new subtitles, voiceovers, or platform‑specific edits.",
    imageUrl: 'https://assets.falcocut.ai/ai-removal/remove-subtitles-and-captions.webp',
    buttonText: 'Remove Video Subtitles',
  },
  {
    title: 'Watermark and Text Removal',
    description:
      'Removing watermarks or on‑screen text can slow your work, but our AI text removal tool clears them with sharp, frame‑level accuracy. It handles static, semi‑transparent, and moving overlays without leaving marks or rough edges. With these distractions gone, teams can update product videos, refresh campaign assets, or prepare clips for new platforms with less effort. Your edited video can now carry fresh visuals, clearer messaging, or any design updates your project calls for.',
    imageUrl:
      'https://assets.falcocut.ai/ai-removal/remove-watermark-and-onscreen-text.webp',
    buttonText: 'Remove Text from Video',
  },
  {
    title: 'AI Logo Eraser',
    description:
      "Teams often need footage that feels neutral and brand‑safe, and our AI cleanup engine makes that possible without cutting or cropping the frame. By clearing logos from intros, outros, and moving shots, it gives you footage that fits a wider range of uses. The rebuilt areas blend in with the scene, so the viewer's focus stays on the message, not the edit. It's easier for anyone to adapt clips for new campaigns, partner updates, presentations, or internal training where old branding no longer applies. With a clean, unbranded base, your video can shift to whatever story or format you need next.",
    imageUrl: 'https://assets.falcocut.ai/ai-removal/remove-logo-from-video.webp',
    buttonText: 'Remove Logo from Video',
  },
  {
    title: 'One‑Click Batch Removal',
    description:
      "Working with multiple files becomes more efficient when you can process them together, and FalcoCut's batch mode enables exactly that. Several videos with the same resolution can be uploaded and cleaned in one pass. Identical removal settings apply across all files without repeating steps. It's a great fit for multi‑lesson courses, long training programs, and any project built from many related clips. Teams managing large libraries can maintain speed and consistency across their workflow.",
    imageUrl: 'https://assets.falcocut.ai/ai-removal/remove-subtitles-and-captions.webp',
    buttonText: 'Remove Video Subtitles',
  },
  {
    title: 'Adaptive Removal Controls',
    description:
      'Different projects require different cleanup approaches, so our AI removal tool offers both full‑screen detection and manual selection. Full‑screen mode works well when overlays appear consistently throughout the video. Manual mode lets you target specific regions, such as timestamps, corner logos or mid‑frame text. You can adjust the selection precisely without disturbing the rest of the scene. You decide exactly which parts of the frame should be cleared.',
    imageUrl: 'https://assets.falcocut.ai/ai-removal/adaptive-removal-controls.webp',
    buttonText: 'Remove Text from Video',
  },
  {
    title: 'Preserve Layout, Motion, and Transitions',
    description:
      "Maintaining the original structure of your video is essential, and FalcoCut keeps pacing and visual flow stable after cleanup. Subtitles or text disappear without shifting frames or breaking the rhythm you worked hard to create. Your motion paths and visual order stay aligned, even when you remove large elements from the scene. Everything holds together so the final edit feels seamless. When you're editing product demos, UI walkthroughs, or timing-sensitive clips, you get a final cut that stays true to your original layout.",
    imageUrl: 'https://assets.falcocut.ai/ai-removal/preserve-layout-and-motion.webp',
    buttonText: 'Remove Text from Video',
  },
  {
    title: 'Restores Background After Erase',
    description:
      'Clean backgrounds matter, and FalcoCut rebuilds erased areas so they blend in seamlessly. Subtitles, watermarks, and on‑screen text disappear without leaving behind smudges or distracting marks. The AI restores natural texture, lighting, and movement so every cleaned section matches the rest of the frame. Even large text blocks or corner logos are removed without hurting the look of your video. With a background that feels untouched, you can reuse or republish your video confidently—viewers stay focused on your story, not the edit.',
    imageUrl:
      'https://assets.falcocut.ai/ai-removal/background-restoration-after-removal.webp',
    buttonText: 'Remove Text from Video',
  },
  {
    title: 'Integrated Editing and Cleanup Flow',
    description:
      "FalcoCut brings your video work into one connected space, so you don't have to jump between different tools to get things done. You can upload clips, clean them, make edits, preview changes, and download the final version all in the same platform. Keeping everything together helps you stay organized and saves time you'd normally spend switching apps. Your videos also stay consistent in style and quality because every step happens in one place. It's a smooth setup for creators, educators, and teams who manage many clips and want a simpler, more unified workflow.",
    imageUrl:
      'https://assets.falcocut.ai/ai-removal/integrated-editing-and-cleanup-flow.webp',
    buttonText: 'Remove Text from Video',
  },
];

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

interface FeatureCardProps {
  feature: FeatureItem;
  isReversed: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ feature, isReversed }) => {
  return (
    <div
      className={`flex flex-col ${isReversed ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-8 pad:gap-10 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden bg-gray-100">
          <Image
            src={feature.imageUrl}
            alt={feature.title}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full">
        <h3 className="text-xl pad:text-2xl font-bold text-gray-900 mb-4">
          {feature.title}
        </h3>
        <p className="text-sm pad:text-base text-gray-600 leading-relaxed mb-6">
          {feature.description}
        </p>
        <button
          type="button"
          onClick={() => scrollToTop()}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
          style={{ background: '#745CFF' }}
        >
          <span>{feature.buttonText}</span>
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
};

const FeaturesSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Header */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
          Powerful Features for Perfect Video Cleanup
        </h2>
        <p className="text-sm pad:text-base text-gray-600 text-center max-w-4xl mx-auto mb-12 pad:mb-16">
          Effortlessly remove subtitles, watermarks, logos, and text from videos with
          FalcoCut. Whether you&apos;re a student, business professional, or social media
          enthusiast, our online subtitle remover clears distractions and delivers clean,
          polished results with ease.
        </p>

        {/* Features List */}
        <div className="space-y-16 pad:space-y-20">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              feature={feature}
              isReversed={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
